import type { BasicCustomerFragment, Maybe } from '@drycut/graphql';

import { Button, CircleButton, Heading, Para, Stack } from '@drycut/ui-components';
import { formatDateTime } from '@drycut/ui-format-utils';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { DateTime, Interval } from 'luxon';
import { Fragment, type PropsWithChildren } from 'react';

import PreferencesList from './PreferencesList';

type Props = PropsWithChildren<{
  customer?: Maybe<BasicCustomerFragment>;
}>;

export default function PreferencesPopover({ customer, children }: Props): React.ReactNode {
  if (!customer) {
    return children;
  }

  if (!customer.preferences.submittedPreferences) {
    return children;
  }

  const lastBooking = DateTime.fromISO(customer.lastBookingDate as string);
  const now = DateTime.now();
  const daysInterval = Interval.fromDateTimes(lastBooking, now);

  return (
    <Popover
      className="relative flex w-full"
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      <PopoverButton className="w-full focus:ring-2 focus:outline-none focus:ring-primary-400">
        {children}
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel className="absolute z-10 left-1/2 w-screen max-w-lg -translate-x-1/2 transform px-4 -mt-36">
          <Stack gap={2} className="bg-white p-4 shadow-2xl text-left">
            <Stack direction="row" className="justify-between">
              <Heading variant="h5" align="left" className="font-semibold">
                {customer.name}
              </Heading>
              <CircleButton as={PopoverButton}>
                <XMarkIcon className="size-6" />
              </CircleButton>
            </Stack>

            <Stack>
              <Para size="sm">
                <strong className="font-aribaubold mr-1">Letzer Besuch:</strong>{' '}
                {formatDateTime(customer.lastBookingDate as string)} (
                {daysInterval.length('days').toFixed()} Tage her)
              </Para>

              <Para size="sm">
                <strong className="font-aribaubold mr-1">Anzahl Besuch:</strong>{' '}
                {customer.bookingsCount}
              </Para>
            </Stack>

            <div className="mt-4">
              <PreferencesList preferences={customer.preferences} />
            </div>

            <Para size="sm" className="mb-4">
              {customer.preferences.notes}
            </Para>

            <Button as={PopoverButton}>Close</Button>
          </Stack>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
